import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from "@material-ui/core";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import {
  ActionLink,
  AvailabilityReviewRating,
  CloseButtonIcon,
  Icon,
  IconName,
  MobilePopoverCard,
  MobileSearchFieldButton,
} from "halifax";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Category,
  ExperiencesAvailabilityAutocompleteResult,
  ParsedGroupedExperienceIds,
} from "redmond";
import { MobileKeywordSearchConnectorProps } from "./container";
import "./styles.scss";
import { PATH_SHOP } from "../../../../utils/paths";
import { transformToStringifiedShopQuery } from "../../../../utils/queryStringHelpers";
import { SEARCH_BY, SEARCH_EXPERIENCES } from "../KeywordSearch/constants";

type CategoryType<ValueType> = {
  results: ValueType[];
  label: string | JSX.Element;
  category: string;
};

export interface IMobileAutocompleteOptionsProps<ValueType> {
  valueCategories: CategoryType<ValueType>[] | null;
  loading?: boolean;
  setSelectedGroupedExperience: (option: ParsedGroupedExperienceIds) => void;
  fromDate: Date | null;
  untilDate: Date | null;
  experiencesByCategory: ParsedGroupedExperienceIds[];
}

export const MobileAvailabilityAutocompleteOptions = <
  ValueType extends ExperiencesAvailabilityAutocompleteResult & {
    categoryLabel: string;
  }
>(
  props: IMobileAutocompleteOptionsProps<ValueType>
): JSX.Element => {
  const {
    loading,
    valueCategories,
    setSelectedGroupedExperience,
    fromDate,
    untilDate,
    experiencesByCategory,
  } = props;

  const getGroupedExperience = (groupedExperienceId: string) =>
    experiencesByCategory.filter(
      (category) => category.tag.tag.id.toString() === groupedExperienceId
    )[0];

  return !loading && valueCategories ? (
    <List
      className="mobile-availability-autocomplete-options-root"
      subheader={<li />}
    >
      {valueCategories.map((option: CategoryType<ValueType>) => (
        <li key={`section-${option.category}`} className="section-header">
          <ul className="list-item">
            <ListSubheader className="group-header">
              {option.label}
            </ListSubheader>
            {option.results.map((item: ValueType, itemIndex) => {
              const selectionIsCategory =
                item.categoryLabel === Category.Categories.toString();

              return (
                <ListItem
                  key={`item-${itemIndex}`}
                  className="group-item-container"
                  onClick={() => {
                    if (selectionIsCategory) {
                      // get category tags
                      setSelectedGroupedExperience(
                        getGroupedExperience(item.id)
                      );
                    } else {
                      window.open(
                        `${PATH_SHOP}${transformToStringifiedShopQuery(
                          { value: item.id.toString() },
                          fromDate,
                          untilDate
                        )}`
                      );
                    }
                  }}
                >
                  <Box className="item-info">
                    <ListItemIcon className="group-item-icon">
                      <Icon
                        name={
                          selectionIsCategory
                            ? "navigation-icon"
                            : "experiences-icon"
                        }
                      />
                    </ListItemIcon>
                    <Box className="group-label-and-reviews-container">
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          className: "list-item-text",
                        }}
                        className="group-item-text"
                      />
                      {item.rating && (
                        <AvailabilityReviewRating
                          reviewCount={item.rating.numberOfReviews}
                          scaledScore={item.rating.reviewAverage}
                          shortReviews
                        />
                      )}
                    </Box>
                  </Box>
                  <FontAwesomeIcon icon={faChevronRight} />
                </ListItem>
              );
            })}
          </ul>
        </li>
      ))}
    </List>
  ) : (
    <Typography variant="body1" className="autocomplete-loading-text">
      Loading
    </Typography>
  );
};

interface IMobileKeywordSearchProps extends MobileKeywordSearchConnectorProps {
  setSelectedGroupedExperience: (option: ParsedGroupedExperienceIds) => void;
}

export const MobileKeywordSearch = (props: IMobileKeywordSearchProps) => {
  const {
    value,
    fetchValueCategories,
    valueCategories,
    setSelectedGroupedExperience,
    loading,
    experiencesByCategory,
    fromDate,
    untilDate,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = React.useState<string>(
    value ? value.label : ""
  );

  const onKeywordSearch = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setInputValue(event.target.value);
    if (event.target.value) {
      fetchValueCategories(event.target.value);
    }
  };

  const onClearLocationField = () => {
    setInputValue("");
    fetchValueCategories("");
  };

  const options = useMemo(() => {
    if (valueCategories && valueCategories?.length > 0) {
      return valueCategories.map((category) => {
        const { results, label: categoryLabel } = category;
        const resultsWithCategory = results.map((result) => ({
          ...result,

          categoryLabel,
        }));
        return { ...category, results: resultsWithCategory };
      });
    }
    return [];
  }, [valueCategories]);

  return (
    <>
      <MobileSearchFieldButton
        value={SEARCH_BY}
        onClick={() => setModalOpen(true)}
        icon={<Icon name={IconName.MagnifyingGlass} />}
      />
      <MobilePopoverCard
        open={modalOpen}
        className="mobile-availability-autocomplete-popup"
        onClose={() => setModalOpen(false)}
        headerElement={
          <Box className="modal-header-container">
            <Typography className="header-title">
              {SEARCH_EXPERIENCES}
            </Typography>
            <ActionLink
              className="modal-close-button"
              onClick={() => setModalOpen(false)}
              content={<CloseButtonIcon className="close-button-icon" />}
              label="Close"
              showTappableArea
            />
          </Box>
        }
        fullScreen
      >
        <Box className="mobile-availability-autocomplete">
          <Box className="mobile-availability-autocomplete-input-field">
            <Icon name={IconName.B2BMapPin} />
            <TextField
              className="availability-autocomplete"
              onChange={(value) => onKeywordSearch(value)}
              value={inputValue}
              label={SEARCH_BY}
              InputProps={{
                disableUnderline: true,
              }}
            />
            <FontAwesomeIcon
              className={clsx("icon", { hidden: !inputValue })}
              icon={faTimesCircle}
              onClick={onClearLocationField}
            />
          </Box>

          <MobileAvailabilityAutocompleteOptions
            valueCategories={
              inputValue || options.find((option) => option?.results.length > 0)
                ? options
                : []
            }
            setSelectedGroupedExperience={setSelectedGroupedExperience}
            fromDate={fromDate}
            untilDate={untilDate}
            loading={loading}
            experiencesByCategory={experiencesByCategory}
          />
        </Box>
      </MobilePopoverCard>
    </>
  );
};
